import React, { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../firebase/interfaces';
import {
  SUPER_ADMIN_ROUTES,
  SHOP_ROUTES,
  TEST_KITS_MANAGEMENT,
  SHOP,
  QR_ACTION,
  QR_REDIRECT,
  FACE_PHOTO,
  MY_SKIN_FACE_CAMERA,
  DASHBOARD_FACE_CAMERA,
  TEST_KITS_MANAGEMENT_BARCODE_CAMERA,
  MY_SKIN_SUBMIT_PHOTO,
  DASHBOARD_SUBMIT_PHOTO,
  MY_SKIN_SCAN_COMPLETED,
  DASHBOARD_SCAN_COMPLETED,
  TEST_KITS_MANAGEMENT_BARCODE_SCANNED,
  TEST_KITS_MANAGEMENT_BARCODE_SCAN_COMPLETED,
  SUPER_ADMIN,
} from 'utilities/routes';

import { QrProtectedRoute } from 'router/QrProtectedRoute';
import NetworkRequiredRoutes from 'components/networkRequiredRoutes';
import RoleRequiredRoute from 'components/RoleRequiredRoute';
import { FallbackLoader } from 'components/loaderComponent';
import { Camera } from 'app/my-skin/components/Camera';
import { BarcodeCamera } from 'app/manage-test-kits/components/BarcodeCamera';
import PatientRoutes from '../../router/patient';
import ProviderRoutes from '../../router/provider';
import AuthenticatedRoute from '../../router/AuthenticatedRoute';

const SuperAdminRoutes = lazy(
  () => import('app/superadmin/routes/SuperAdminRoutes'),
);

const AppointmentFacePhotoRoutes = lazy(
  () => import('../my-appointments/pages/face-photo/routes'),
);
const PhotoSubmission = lazy(
  () => import('app/my-skin/components/PhotoSubmission'),
);
const ScanCompleted = lazy(
  () => import('app/my-skin/components/ScanCompleted'),
);
const TestKitBarcodeScanned = lazy(
  () => import('app/manage-test-kits/components/TestKitBarcodeScanned'),
);
const BarcodeScanCompleted = lazy(
  () => import('app/manage-test-kits/components/BarcodeScanCompleted'),
);
const QrAction = lazy(() => import('app/qr-action/QrAction'));
const QrRedirect = lazy(() => import('app/qr-action/QrRedirect'));

const RoleBasedRoutes: React.FC = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.role) {
      const reloadTimer = setTimeout(() => {
        window.location.reload();
      }, 5_000);
      return () => clearTimeout(reloadTimer);
    }
  }, [user?.role]);

  switch (user?.role) {
    case Roles.PATIENT:
      return <PatientRoutes />;
    case Roles.PROVIDER:
      return <ProviderRoutes />;
    case Roles.SUPERADMIN:
    case Roles.DELEGATED_ADMIN:
      return <Navigate to={SUPER_ADMIN} replace />;
    default:
      return <FallbackLoader />;
  }
};

export const AppRoutes = () => {
  return (
    <Suspense fallback={<FallbackLoader />}>
      <Routes>
        <Route
          path={SUPER_ADMIN_ROUTES}
          element={
            <NetworkRequiredRoutes>
              <RoleRequiredRoute
                allowedRoles={[Roles.SUPERADMIN, Roles.DELEGATED_ADMIN]}
              >
                <SuperAdminRoutes />
              </RoleRequiredRoute>
            </NetworkRequiredRoutes>
          }
        />

        <Route path={QR_ACTION} element={<QrAction />} />
        <Route path={`${QR_REDIRECT}/:token`} element={<QrRedirect />} />
        <Route
          path={`${FACE_PHOTO}/*`}
          element={
            <QrProtectedRoute>
              <AppointmentFacePhotoRoutes />
            </QrProtectedRoute>
          }
        />
        <Route
          path={MY_SKIN_FACE_CAMERA}
          element={
            <QrProtectedRoute>
              <Camera />
            </QrProtectedRoute>
          }
        />
        <Route
          path={DASHBOARD_FACE_CAMERA}
          element={
            <QrProtectedRoute>
              <Camera />
            </QrProtectedRoute>
          }
        />
        <Route
          path={TEST_KITS_MANAGEMENT_BARCODE_CAMERA}
          element={
            <QrProtectedRoute>
              <BarcodeCamera />
            </QrProtectedRoute>
          }
        />
        <Route
          path={MY_SKIN_SUBMIT_PHOTO}
          element={
            <QrProtectedRoute>
              <PhotoSubmission />
            </QrProtectedRoute>
          }
        />
        <Route
          path={DASHBOARD_SUBMIT_PHOTO}
          element={
            <QrProtectedRoute>
              <PhotoSubmission />
            </QrProtectedRoute>
          }
        />
        <Route
          path={MY_SKIN_SCAN_COMPLETED}
          element={
            <QrProtectedRoute>
              <ScanCompleted />
            </QrProtectedRoute>
          }
        />
        <Route
          path={DASHBOARD_SCAN_COMPLETED}
          element={
            <QrProtectedRoute>
              <ScanCompleted />
            </QrProtectedRoute>
          }
        />
        <Route
          path={TEST_KITS_MANAGEMENT_BARCODE_SCANNED}
          element={
            <QrProtectedRoute>
              <TestKitBarcodeScanned />
            </QrProtectedRoute>
          }
        />
        <Route
          path={TEST_KITS_MANAGEMENT_BARCODE_SCAN_COMPLETED}
          element={
            <QrProtectedRoute>
              <BarcodeScanCompleted />
            </QrProtectedRoute>
          }
        />

        <Route
          path={SHOP_ROUTES}
          element={<Navigate to={`${TEST_KITS_MANAGEMENT}${SHOP}`} replace />}
        />

        <Route
          path="/*"
          element={
            <AuthenticatedRoute>
              <NetworkRequiredRoutes>
                <RoleBasedRoutes />
              </NetworkRequiredRoutes>
            </AuthenticatedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};
